import { useCallback, useRef } from 'react';
import { confirmDialog, useGlobalToast } from '@repo/atomic-components';
import { useLazyGetDocumentLightBundlePageCountQuery } from '@/api';

interface ConfirmDocumentDeleteParams {
  /** Id of the mounted `WarningConfirmationDialog` to open. */
  dialogId: string;
  /** Case document id — only case documents can have light bundle pages. */
  documentId?: string;
  unassignedDocumentId?: string;
}

/**
 * Opens the delete-document confirm. For case documents it first checks
 * how many pages are already extracted to the light bundle and, if any,
 * swaps the dialog copy to call the count out. Resolves `false` (with an
 * error toast) when that check fails, so a document is never deleted
 * without the warning having been shown.
 *
 * The table stays clickable while the count loads, so only the latest
 * click may open the dialog — otherwise a slow response for document A
 * could replace B's dialog and confirming would delete A.
 */
export const useConfirmDocumentDelete = () => {
  const [triggerGetDocumentLightBundlePageCount] = useLazyGetDocumentLightBundlePageCountQuery();
  const globalToastRef = useGlobalToast();
  const latestRequestIdRef = useRef(0);

  return useCallback(
    async ({ dialogId, documentId, unassignedDocumentId }: ConfirmDocumentDeleteParams) => {
      const requestId = ++latestRequestIdRef.current;
      let pagesInLightBundle = 0;

      if (documentId) {
        try {
          ({ pagesInLightBundle } = await triggerGetDocumentLightBundlePageCount({ documentId }).unwrap());
        } catch (error) {
          if (requestId !== latestRequestIdRef.current) return false;
          console.error('Failed to check light bundle pages:', error);
          globalToastRef.current?.show({
            severity: 'error',
            summary: 'Delete failed',
            detail: 'Could not check the light bundle for this document. Please try again.',
            closable: true,
            life: 5000,
          });
          return false;
        }

        // A newer delete click came in while this count was loading.
        if (requestId !== latestRequestIdRef.current) return false;
      }

      const bodyText =
        pagesInLightBundle > 0 ? (
          <>
            <span className="font-semibold">{pagesInLightBundle}</span>{' '}
            {pagesInLightBundle === 1 ? 'page of this document has' : 'pages of this document have'} been extracted to
            the light bundle. The document will be permanently deleted.
          </>
        ) : undefined;

      return confirmDialog({ id: dialogId, documentId, unassignedDocumentId, bodyText });
    },
    [triggerGetDocumentLightBundlePageCount, globalToastRef],
  );
};
